import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, logout, getUserPermissions } from '../Api/api';
import { jwtDecode } from 'jwt-decode';
import { hasPermission, hasAnyPermission, getPermittedRegions, filterByRegionPermission } from '../../utils/permissionUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(() => {
    // Get initial region from localStorage or default to "1" (Mumbai)
    return localStorage.getItem('selectedRegion') || "1";
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('Token exists:', !!token);
        if (token) {
          // Get user data and permissions from localStorage if they exist
          const cachedUserData = localStorage.getItem('userData');
          const cachedPermissions = localStorage.getItem('permissions');
          
          if (cachedUserData && cachedPermissions) {
            setUser(JSON.parse(cachedUserData));
            setPermissions(JSON.parse(cachedPermissions));
          } else {
            // If no cached data, fetch fresh data
            try {
              const userData = await getCurrentUser();
              setUser(userData);
              
              const decodedToken = jwtDecode(token);
              const userId = decodedToken.sub || decodedToken.user_id || decodedToken.id || userData.id;
              
              if (userId) {
                const userPermissions = await getUserPermissions(userId);
                setPermissions(userPermissions);
                
                // Cache the data
                localStorage.setItem('userData', JSON.stringify(userData));
                localStorage.setItem('permissions', JSON.stringify(userPermissions));
              } else {
                console.error('Could not extract user ID from token or user data');
                handleLogout();
              }
            } catch (error) {
              console.error('Error fetching user data or permissions:', error);
              handleLogout();
            }
          }
        } else {
          console.log('No token found, user is not authenticated');
          handleLogout();
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        handleLogout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const handleLogout = () => {
    logout();
    setUser(null);
    setPermissions(null);
    localStorage.removeItem('userData');
    localStorage.removeItem('permissions');
    navigate('/login');
  };

  // Function to handle successful login
  const handleLoginSuccess = (loginData) => {
    const { userData, permissions } = loginData;
    setUser(userData);
    setPermissions(permissions);
    
    // Cache the data
    localStorage.setItem('userData', JSON.stringify(userData));
    localStorage.setItem('permissions', JSON.stringify(permissions));
    
    // Navigate to home page or dashboard
    navigate('/');
  };

  // Check if user has permission for a specific resource and action
  const checkPermission = (resource, action) => {
    console.log(`Checking permission for ${resource}:${action}`, permissions);
    return hasPermission(permissions, resource, action);
  };

  // Check if user has permission for a specific resource and any of the provided actions
  const checkAnyPermission = (resource, actions) => {
    console.log(`Checking any permission for ${resource}:${actions}`, permissions);
    return hasAnyPermission(permissions, resource, actions);
  };

  // Get the regions a user has permission for a specific resource and action
  const getPermittedRegionsForAction = (resource, action) => {
    return getPermittedRegions(permissions, resource, action);
  };

  // Filter items based on region permissions
  const filterItemsByRegionPermission = (resource, action, items, getItemRegion) => {
    return filterByRegionPermission(permissions, resource, action, items, getItemRegion);
  };

  // Add function to trigger data refresh
  const triggerDataRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Update the updateSelectedRegion function
  const updateSelectedRegion = (regionId) => {
    setSelectedRegion(regionId);
    localStorage.setItem('selectedRegion', regionId);
    triggerDataRefresh(); // Trigger data refresh when region changes
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      permissions,
      hasPermission: checkPermission,
      hasAnyPermission: checkAnyPermission,
      getPermittedRegions: getPermittedRegionsForAction,
      filterByRegionPermission: filterItemsByRegionPermission,
      handleLoginSuccess,
      logout: handleLogout,
      selectedRegion,
      updateSelectedRegion,
      refreshTrigger // Add refreshTrigger to context
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const PrivateRoute = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  return user ? children : null;
};

// New component for permission-based routes
export const PermissionRoute = ({ children, resource, action }) => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    console.log('PermissionRoute check:', { user, resource, action });
    
    const hasAccess = hasPermission(resource, action);
    console.log('Has permission:', hasAccess);
    
    if (!user) {
      console.log('No user, redirecting to login');
      navigate('/login');
    } else if (!hasAccess) {
      console.log('No permission, redirecting to unauthorized');
      navigate('/unauthorized');
    }
  }, [user, hasPermission, resource, action, navigate]);

  return (user && hasPermission(resource, action)) ? children : null;
}; 